import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useFilters, useSortBy, useTable } from 'react-table'
import LoadingSpinner from '../../elem/LoadingSpinner'
import {
    FaChevronDown,
    FaChevronUp,
    FaCopy,
    FaEdit,
    FaTrashAlt,
    FaTimes,
} from 'react-icons/fa'
import { Link } from 'react-router-dom'

import { ReportingContext } from '../../wrappers/ReportingContext'
import { formatDate } from '../../../utils/valueFormatting'
import dayjs from 'dayjs'
import ConfirmationModal from './ConfirmationModal'
import DefaultTooltip from '../../elem/Tooltip'
import {
    operatorFilterFunction,
    getDefaultOperatorForType,
} from '../../../utils/filters/filterOperators'
import FilterOperatorSelect from '../../elem/filters/FilterOperatorSelect'

const Cell = ({ value, name, description, onDeleteClick }) => {
    const { cloneReport } = useContext(ReportingContext)

    const onDelete = useCallback(() => {
        onDeleteClick(value, name)
    }, [value, name, onDeleteClick])

    const onClone = useCallback(() => {
        cloneReport(value)
    }, [value])

    return (
        <>
            <Link
                to={{
                    pathname: `/admin/report/edit/${value}`,
                    dashboardProps: {
                        id: value,
                        DashboardName: name,
                        DashboardDescription: description,
                    },
                }}
            >
                <button
                    className="button is-small is-info actionButton"
                    data-tooltip-content={'Edit'}
                    data-tooltip-id={'report-edit'}
                >
                    <FaEdit />
                </button>
            </Link>
            <button
                onClick={onClone}
                className="button is-small is-info actionButton"
                data-tooltip-content={'Clone'}
                data-tooltip-id={'report-clone'}
            >
                <FaCopy />
            </button>
            <button
                onClick={onDelete}
                className="button is-small is-info actionButton"
                data-tooltip-content={'Delete'}
                data-tooltip-id={'report-delete'}
            >
                <FaTrashAlt />
            </button>
        </>
    )
}

export const TooltipProvider = () => (
    <>
        <DefaultTooltip id="report-edit" />
        <DefaultTooltip id="report-clone" />
        <DefaultTooltip id="report-delete" />
    </>
)

const TagCell = ({ tags }) => {
    const tagList = tags ? tags.split(',') : []
    return (
        <div>
            {tagList.map((tag) => (
                <span key={tag} className="tag is-info marginRightSmall">
                    {tag}
                </span>
            ))}
        </div>
    )
}

const getFilterInputType = (fieldType, operator) => {
    if (fieldType === 'date') return operator === 'CONTAINS' ? 'text' : 'date'
    if (fieldType === 'number')
        return operator === 'CONTAINS' ? 'text' : 'number'
    return 'text'
}

const DefaultColumnFilter = ({
    column: { filterValue, preFilteredRows, setFilter, fieldType },
}) => {
    const count = preFilteredRows.length
    const initOperator =
        filterValue && typeof filterValue === 'object'
            ? filterValue.operator
            : getDefaultOperatorForType(fieldType)
    const initValue =
        filterValue && typeof filterValue === 'object'
            ? filterValue.value || ''
            : filterValue || ''
    const initValue2 =
        filterValue && typeof filterValue === 'object'
            ? filterValue.value2 || ''
            : ''

    const [operator, setOperator] = useState(initOperator)
    const [value, setValue] = useState(initValue)
    const [value2, setValue2] = useState(initValue2)

    const updateFilter = useCallback(
        (op, v1, v2) => {
            if (op !== 'BLANK' && !v1 && v1 !== 0) {
                setFilter(undefined)
                return
            }
            setFilter({
                operator: op,
                value: v1,
                value2: v2 || undefined,
                fieldType: fieldType || 'string',
            })
        },
        [setFilter, fieldType]
    )

    const onOperatorChange = useCallback(
        (op) => {
            setOperator(op)
            if (op === 'BLANK') {
                setValue('')
                setValue2('')
                updateFilter('BLANK', '', '')
                return
            }
            const isDate = (fieldType || 'string') === 'date'
            const switchedToDate =
                isDate && op !== 'CONTAINS' && operator === 'CONTAINS'
            const switchedFromDate =
                isDate && op === 'CONTAINS' && operator !== 'CONTAINS'
            const v1 = switchedToDate || switchedFromDate ? '' : value
            if (switchedToDate || switchedFromDate) setValue('')
            const newValue2 = op === 'BETWEEN' ? value2 : ''
            if (op !== 'BETWEEN') setValue2('')
            updateFilter(op, v1, newValue2)
        },
        [value, value2, operator, fieldType, updateFilter]
    )

    const onValueChange = useCallback(
        (e) => {
            const v = e.target.value
            setValue(v)
            updateFilter(operator, v, value2)
        },
        [operator, value2, updateFilter]
    )

    const onValue2Change = useCallback(
        (e) => {
            const v = e.target.value
            setValue2(v)
            updateFilter(operator, value, v)
        },
        [operator, value, updateFilter]
    )

    const clearFilter = useCallback(() => {
        setValue('')
        setValue2('')
        setFilter(undefined)
    }, [setFilter])

    useEffect(() => {
        if (!filterValue) {
            setValue('')
            setValue2('')
        }
    }, [filterValue, fieldType])

    const inputType = getFilterInputType(fieldType, operator)

    return (
        <>
            <FilterOperatorSelect
                fieldType={fieldType || 'string'}
                value={operator}
                onChange={onOperatorChange}
            />
            <div className="control is-expanded has-icons-right">
                <input
                    value={value}
                    onChange={onValueChange}
                    type={inputType}
                    disabled={operator === 'BLANK'}
                    className="input is-small"
                    placeholder={
                        inputType === 'date' ? '' : `Search ${count} records...`
                    }
                />
                <span
                    className="icon is-right"
                    style={{ pointerEvents: 'auto', cursor: 'pointer', color: '#9e9e9e' }}
                    onClick={clearFilter}
                >
                    <FaTimes size={12} />
                </span>
            </div>
            {operator === 'BETWEEN' && (
                <input
                    value={value2}
                    onChange={onValue2Change}
                    type={inputType}
                    className="input is-small filterBetweenInput"
                    placeholder={inputType === 'date' ? '' : 'End value...'}
                />
            )}
        </>
    )
}

const createColumns = (onDeleteClick, roles) => {
    const baseColumns = [
        {
            Header: 'Report ID',
            accessor: 'ReportId',
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'number',
            sortType: 'basic',
        },
        {
            Header: 'Report Name',
            accessor: 'ReportName',
            Cell: ({ cell, row }) => (
                <Link to={`/admin/report/${row.original.ReportId}`}>
                    {row.original.ReportName}
                </Link>
            ),
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'string',
            sortType: 'basic',
        },
        {
            Header: 'Report Description',
            accessor: 'ReportDescription',
            maxWidth: 180,
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'string',
            sortType: 'basic',
        },
        {
            Header: 'Tags',
            accessor: 'Tags',
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'string',
            disableSortBy: true,
            Cell: ({ cell }) => <TagCell tags={cell.value} />,
        },
        {
            Header: () => 'Modify Date',
            id: 'UpdatedDate',
            accessor: (row) => formatDate(row['UpdatedDate']),
            Cell: ({ cell }) => {
                return dayjs(cell.value)
                    .format('MM/DD/YYYY')
                    .includes('01/01/1002')
                    ? ''
                    : dayjs(cell.value).format('MM/DD/YYYY')
            },
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'date',
            sortType: (a, b) => {
                return (
                    dayjs(b.values['UpdatedDate']).toDate() -
                    dayjs(a.values['UpdatedDate']).toDate()
                )
            },
        },
        {
            Header: 'Last Modified By',
            accessor: 'UpdatedBy',
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'string',
            sortType: 'basic',
        },
        {
            Header: () => 'Create Date',
            id: 'CreatedDate',
            accessor: (row) => formatDate(row['CreatedDate']),
            Cell: ({ cell }) => {
                return dayjs(cell.value)
                    .format('MM/DD/YYYY')
                    .includes('01/01/1002')
                    ? ''
                    : dayjs(cell.value).format('MM/DD/YYYY')
            },
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'date',
            sortType: (a, b) => {
                return (
                    dayjs(b.values['CreatedDate']).toDate() -
                    dayjs(a.values['CreatedDate']).toDate()
                )
            },
        },
        {
            Header: 'Created By',
            accessor: 'CreatedBy',
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: 'string',
            sortType: 'basic',
        },
        {
            Header: 'Actions',
            accessor: (row) => row.ReportId,
            disableFilters: true,
            disableSortBy: true,
            minWidth: 130,
            width: 130,
            maxWidth: 130,
            Cell: ({ cell, row }) => (
                <Cell
                    name={row.original.ReportName}
                    description={row.original.ReportDescription}
                    value={cell.value}
                    onDeleteClick={onDeleteClick}
                />
            ),
        },
    ]
    return baseColumns
}

const ReportingTable = () => {
    const { loading, reports, deleteReport } = useContext(ReportingContext)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [reportToDelete, setReportToDelete] = useState(null)
    const [reportNameToDelete, setReportNameToDelete] = useState('')

    const data = reports ? reports : null

    const handleDeleteClick = useCallback((reportId, reportName) => {
        setReportToDelete(reportId)
        setReportNameToDelete(reportName)
        setShowDeleteModal(true)
    }, [])

    const confirmDelete = useCallback(() => {
        if (reportToDelete) {
            deleteReport(reportToDelete)
        }
        setShowDeleteModal(false)
        setReportToDelete(null)
        setReportNameToDelete('')
    }, [reportToDelete, deleteReport])

    const cancelDelete = useCallback(() => {
        setShowDeleteModal(false)
        setReportToDelete(null)
        setReportNameToDelete('')
    }, [])

    const columns = useMemo(
        () => createColumns(handleDeleteClick),
        [handleDeleteClick]
    )

    const tableData = useMemo(() => data, [data])
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({ columns, data: tableData }, useFilters, useSortBy)

    if (loading || !reports) {
        return <LoadingSpinner />
    }

    return (
        <>
            <div className="adminTable content reportingTableContainer">
                <div className="table-container">
                    <table
                        {...getTableProps()}
                        className="table is-fullwidth is-striped is-narrow"
                    >
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th {...column.getHeaderProps()}>
                                            <div className="tableHeaderWrapper reportingTableHeaderWrapper">
                                                <div>
                                                    <span
                                                        {...column.getSortByToggleProps()}
                                                    >
                                                        {column.render(
                                                            'Header'
                                                        )}{' '}
                                                        <span className="">
                                                            {column.isSorted ? (
                                                                column.isSortedDesc ? (
                                                                    <FaChevronUp fontSize="0.75rem" />
                                                                ) : (
                                                                    <FaChevronDown fontSize="0.75rem" />
                                                                )
                                                            ) : (
                                                                ''
                                                            )}
                                                        </span>
                                                    </span>
                                                </div>
                                                {column && column.canFilter ? (
                                                    <div className="tableFilterWrapper">
                                                        {column.render(
                                                            'Filter'
                                                        )}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map((row) => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => {
                                            return (
                                                <td
                                                    {...cell.getCellProps({
                                                        style: {
                                                            minWidth:
                                                                cell.column
                                                                    .minWidth,
                                                            width: cell.column
                                                                .width,
                                                            maxWidth:
                                                                cell.column
                                                                    .maxWidth,
                                                        },
                                                    })}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <TooltipProvider />
            <ConfirmationModal
                display={showDeleteModal}
                title="Delete Confirmation"
                confirmAction={confirmDelete}
                confirmText="Delete"
                denyAction={cancelDelete}
                denyText="Cancel"
            >
                <p>
                    Do you wish to delete Report ID{' '}
                    <strong>
                        {reportToDelete} - {reportNameToDelete}
                    </strong>
                    ?
                </p>
            </ConfirmationModal>
        </>
    )
}
export default ReportingTable
